import React, { useState, createContext, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { when, observable, flow } from 'mobx';
import _set from 'lodash.set';

import Sizes from '../definitions/Sizes';
import Colours from '../definitions/Colours';
import useSessionStore from '../hooks/useSessionStore';
import useDebouncedCallback from '../hooks/useDebouncedCallback';
import noticeError from '../functions/noticeError';
import Form from './Form';
import CircleSpinner from './CircleSpinner';


const Context = createContext();

function Calculator({ title, children, fetchDocument, onSaveDocument, onCreateCalculatorState, ...otherProps }) {
    const sessionStore = useSessionStore();
    const [ state ] = useState(() => createLocalState(sessionStore, fetchDocument));

    const document = state?.document;
    const handleFormChange = useDebouncedCallback(
        (_, formState) => {
            if (onSaveDocument == null) {
                return;
            }

            const values = formState?.fields
                .filter(field => !field.isEmpty && field.isValid)
                .reduce((result, field) => _set(result, field.name, field.typedValue), {});

            onSaveDocument(document, values);
        },
        [ document, onSaveDocument ],
    );

    useEffect(() => {
        onCreateCalculatorState?.(state);
    }, [ state ]);

    return (
        <div className="rh-position-relative">
            <If condition={!state?.isReady}>
                <CircleSpinner />
            </If>
            <StyledForm
                onChange={handleFormChange}
                {...otherProps}
            >
                <div className="title">
                    <h2 className="rh-my-1 rh-mx-0_5 rh-text-m rh-fg-coconut weight-regular">
                        {title}
                    </h2>
                </div>

                <Context.Provider value={state}>
                    {children}
                </Context.Provider>
            </StyledForm>
        </div>
    );
}

Calculator.propTypes = {
    title: PropTypes.node.isRequired,
    children: PropTypes.node.isRequired,
    fetchDocument: PropTypes.func,
    onSaveDocument: PropTypes.func,
    onCreateCalculatorState: PropTypes.func,
    onCreateFormState: PropTypes.func,
};


const StyledForm = styled(Form)`
    display: flex;
    flex-direction: column;

    margin: 0 auto;
    width: 100%;

    > .title {
        background-color: ${Colours.BLUEBERRY_DARK};

        margin-bottom: ${Sizes.SPACING.HALF};
        border-radius: 6px;
        box-shadow: 0 2px 11px 0 ${Colours.STONE};

        text-align: center;
    }
`;


function createLocalState(sessionStore, fetchDocument) {
    const state = observable({
        steps: [],
        document: null,
        hasFetchDocumentError: false,

        getStep(number) {
            return state.steps.find(step => step.number === number);
        },
        getNextStep(number) {
            return state.getStep(number + 1);
        },
        getPreviousStep(number) {
            return state.getStep(number - 1);
        },
        hasCompletedPreviousStep(number, shouldThrowIfNotFound = true) {
            // SPECIAL CASE: Cannot evaluate if we have no steps - assuming this is
            // being called before the steps get a chance to add themselves to the calculator
            if (state.steps.length === 0) {
                return false;
            }

            // SPECIAL CASE: we're the first step, act as if "previous" was complete.
            if (number === 1) {
                return true;
            }

            const previousStep = state.getPreviousStep(number);

            // If this throws, it means that there's a step number missing from our calculator
            if (previousStep == null) {
                if (shouldThrowIfNotFound) {
                    const error = new RangeError(`[hasCompletedPreviousStep] Unable to find a step previous to ${number}`);

                    noticeError(error, {
                        steps: state.steps,
                    });
                    throw error;
                } else {
                    return false;
                }
            }

            return previousStep.isComplete;
        },

        addStep(step) {
            if (step?.number == null) {
                const stepError = new RangeError('[addStep] Step number is required');

                noticeError(stepError);
                throw stepError;
            } else if (state.steps.some(existingStep => existingStep.number === step.number)) {
                const existingStepError = new RangeError(`[addStep] Step numbers must be unique. We already contain step ${step.number}`);

                noticeError(existingStepError, {
                    steps: state.steps,
                });
                throw existingStepError;
            }

            state.steps.push(step);
        },
        removeStep(number) {
            const stepIndex = state.steps.findIndex(step => step.number === number);

            // findIndex returns -1 if it cannot find the item
            if (stepIndex === -1) {
                const error = new RangeError(`[removeStep] Unable to remove step ${number}`);

                noticeError(error, {
                    steps: state.steps,
                });
                throw error;
            }

            state.steps.splice(stepIndex, 1);
        },

        get isReady() {
            // We don't want to fetch document in Wordpress, hence returning true right away
            if (typeof fetchDocument !== 'function' || (typeof window !== 'undefined' && window?.wp)) {
                return true;
            }

            return state.document != null || state.hasFetchDocumentError === true;
        },

        get isUserLoggedIn() {
            return sessionStore.isLoggedIn;
        },

        fetchDocument: flow(function* () {
            try {
                const document = yield fetchDocument();

                // clear out saved data for guest users
                if (document && !state.isUserLoggedIn) {
                    document.journey.update({
                        calculators: null,
                    });
                }

                state.document = document;
            } catch (error) {
                state.hasFetchDocumentError = true;
            }
        }),
    });

    // We don't want to fetch document in Wordpress, hence the window.wp check
    if (typeof fetchDocument === 'function' && (typeof window !== 'undefined' && !window?.wp)) {
        sessionStore.fetchSession();

        when(
            () => sessionStore.hasFetchedSession === true,
            () => state.fetchDocument(),
        );
    }

    return state;
}

export { Context };
export default observer(Calculator);
